import { z } from 'zod'
import { SupportedComponents, SupportedLayoutComponents } from './types'
import type { InteractiveFormElementSubController } from './InteractiveFormElementSubController'

const requiredPropertySchema = z.object({
  // this is pretty much the only necessary part of the property schema
  // everything else is delegated for the component to handle
  _reactComponent: z
    .object({
      // we use the enum to ensure we support the components
      name: z.nativeEnum(SupportedComponents),
      title: z.string().optional(),
    })
    .optional(),
  _fieldMeta: z
    .object({
      requestAutocomplete: z.boolean().optional(),
    })
    .optional(),
})

const validationSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('string'),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
  }),
  z.object({
    type: z.literal('number'),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
  }),
  z.object({
    type: z.literal('boolean'),
  }),
  z.object({
    type: z.literal('array'),
    minItems: z.number().optional(),
    maxItems: z.number().optional(),
    items: z.object({
      type: z.literal('string').or(z.literal('object')),
    }),
  }),
])

const passThroughSchema = z.record(z.string(), z.unknown())

const propertySchema = z.intersection(requiredPropertySchema, passThroughSchema)

const layoutSchema = z
  .object({
    groups: z
      .array(
        z.object({
          id: z.string(),
          component: z.nativeEnum(SupportedLayoutComponents),
          title: z.string().optional(),
          fields: z.array(z.string()),
        })
      )
      .optional(),
  })
  .optional()

export const formSchema = z.object({
  title: z.string().optional(),
  type: z.literal('object'),
  required: z.array(z.string()).optional(),
  properties: z.record(z.string(), propertySchema),
  _layout: layoutSchema,
  _generationRules: z
    .object({
      triggerFields: z.array(z.string()).optional(),
      autoSuggestFields: z.array(z.string()).optional(),
      neverSuggestFields: z.array(z.string()).optional(),
    })
    .optional(),
})

export const formValidationSchema = z.object({
  title: z.string().optional(),
  type: z.literal('object'),
  required: z.array(z.string()).optional(),
  properties: z.record(
    z.string(),
    z.intersection(requiredPropertySchema, validationSchema)
  ),
  _layout: layoutSchema,
})

export type InteractiveFormSchema = z.infer<typeof formSchema>
export type InteractiveFormValidationSchema = z.infer<
  typeof formValidationSchema
>
export type InteractiveFormProperty = z.infer<typeof propertySchema>

export type InteractiveFormPropertyGroup = {
  id: string
  component: SupportedLayoutComponents
  title?: string
  properties: Record<string, InteractiveFormProperty>
}

export type InteractiveFormComponentProps = {
  subController: InteractiveFormElementSubController
  fieldName: string
  prop: InteractiveFormProperty
}

export type InteractiveFormGroupComponentProps = {
  subController: InteractiveFormElementSubController
  group: InteractiveFormPropertyGroup
}

export const aiResponseSchema = z.object({
  agentId: z.string(),
  formElementId: z.string(),
  requestId: z.string(),
  response: z.string().optional(),
  error: z.string().optional(),
})

export type AIResponse = z.infer<typeof aiResponseSchema>
